import React, { memo } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Box,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";

interface DrawerItem {
  id: string;
  text: string;
  icon: React.ReactNode;
  path?: string;
  children?: DrawerItem[];
  selectable?: boolean;
}

interface DrawerProps {
  items: DrawerItem[];
  selectedItemId: string | null;
  openItemId: string | null;
  handleListItemClick: (id: string, path?: string) => void;
  toggleOpen: (id: string) => void;
  miniMode?: boolean | null;
}

const LargeScreenDrawer: React.FC<DrawerProps> = ({
  items,
  selectedItemId,
  openItemId,
  handleListItemClick,
  toggleOpen,
  miniMode,
}) => {
  const theme = useTheme(); // Use theme for overall layout and non-text elements

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <List sx={{ opacity: miniMode ? 0.5 : 1 }}>
        {items.map((item) => (
          <DrawerListItem
            key={item.id}
            item={item}
            selectedItemId={selectedItemId}
            openItemId={openItemId}
            handleListItemClick={handleListItemClick}
            toggleOpen={toggleOpen}
            miniMode={miniMode}
          />
        ))}
      </List>
    </Box>
  );
};

// Memoized drawer list item for performance improvement
const DrawerListItem = memo(
  ({
    item,
    selectedItemId,
    openItemId,
    handleListItemClick,
    toggleOpen,
    miniMode,
  }: any) => {
    return (
      <>
        <ListItem
          disablePadding
          sx={{
            minHeight: "64px",
            backgroundColor:
              selectedItemId === item.id
                ? "#4a4a4a" // Darker background for selected item
                : "transparent",
          }}
          onClick={() => {
            if (item.selectable || !item.children) {
              handleListItemClick(item.id, item.path);
            } else {
              toggleOpen(item.id);
            }
          }}
        >
          <ListItemButton>
            <ListItemIcon sx={{ color: "#FFFFFF" /* Pure white for icons */ }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{
                color: "#FFFFFF" /* Pure white for text */,
                display: miniMode ? "none" : "block", // Hide text if miniMode
              }}
              primaryTypographyProps={{
                sx: {
                  color: "#FFF", // Overrides Typography color to white
                },
              }}
            />
            {item.children &&
              (openItemId === item.id ? (
                <ExpandLess
                  sx={{
                    display: miniMode ? "none" : "block", // Hide text if miniMode
                    color: "#FFFFFF",
                  }}
                />
              ) : (
                <ExpandMore
                  sx={{
                    display: miniMode ? "none" : "block", // Hide text if miniMode
                    color: "#FFFFFF",
                  }}
                />
              ))}
          </ListItemButton>
        </ListItem>
        {item.children && (
          <Collapse in={openItemId === item.id} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((childItem: any) => (
                <ListItem
                  key={childItem.id}
                  disablePadding
                  sx={{
                    pl: miniMode ? 1 : 4, // No padding in miniMode, default otherwise
                    backgroundColor:
                      selectedItemId === childItem.id
                        ? "#4a4a4a" // Darker background for selected child
                        : "transparent",
                  }}
                  onClick={() =>
                    handleListItemClick(childItem.id, childItem.path)
                  }
                >
                  <ListItemButton>
                    <ListItemIcon
                      sx={{ color: "#FFFFFF" /* White icon for children */ }}
                    >
                      {childItem.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={childItem.text}
                      sx={{
                        color: "#FFFFFF" /* White text for children */,
                        maxWidth: "calc(100% - 56px)", // Prevent overflow for child items
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: miniMode ? "none" : "block", // Hide text in miniMode
                      }}
                      primaryTypographyProps={{
                        sx: {
                          display: miniMode ? "none" : "block", // Hide text if miniMode
                          color: "#FFF", // Overrides Typography color to white
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  }
);

const SmallScreenDrawer: React.FC<DrawerProps> = ({
  items,
  selectedItemId,
  openItemId,
  handleListItemClick,
  toggleOpen,
  miniMode,
}) => {
  return (
    <List>
      {items.map((item) => (
        <DrawerListItem
          key={item.id}
          item={item}
          selectedItemId={selectedItemId}
          openItemId={openItemId}
          handleListItemClick={handleListItemClick}
          toggleOpen={toggleOpen}
          miniMode={false}
        />
      ))}
    </List>
  );
};

export { LargeScreenDrawer, SmallScreenDrawer };
